/**
 * The jump scare itself: a full-viewport overlay that either (a) zooms/shakes into an
 * original procedural face (or an operator-provided drop-in image) while a synthesized
 * scream plays, dismissing on click, `Escape`, or a 4s timeout — `play()` — or (b) plays
 * the operator's full-screen level-3 scare video with its own sound, dismissing on
 * click, `Escape`, or the video's own `ended` event (no 4s cutoff — the video owns its
 * own ~10s length) — `playVideo()`. See `public/scare/ASSETS.md` for asset provenance
 * and `client/scare/face.ts` / `client/scare/scream.ts` for the two pieces `play()`
 * composes.
 *
 * The animation is presentation only — nothing here can block the caller: neither
 * `play()` nor `playVideo()` ever throws, and a missing 2D context, a failed image
 * load, or a `synthScream` scheduling error all degrade silently rather than wedging
 * the promise they return. `playVideo()` itself falls back to `play()` (the built-in
 * scare) whenever the video never became available — see `armedMode`/`resolveArmedMode`
 * — so level 3 never shows nothing.
 *
 * Accessibility: the overlay is `role="dialog"` + `aria-modal="true"` with an
 * `aria-label` describing the scare and how to dismiss it, and `tabindex="-1"`
 * so it's a valid programmatic focus target without joining the page's tab
 * order. `play()`/`playVideo()` remember whatever had focus beforehand and move focus
 * to the overlay itself; `dismiss()` restores it. There's only one focusable element
 * inside the overlay (the overlay itself — neither the canvas nor the video is
 * interactive), so Tab is trapped on it via the pure `trapTabKey` (see
 * `focus-trap.ts`), same mechanism the warning dialog uses.
 */

import { drawFace } from "./face";
import { trapTabKey } from "./focus-trap";
import { synthScream } from "./scream";

const ZOOM_DURATION_MS = 1500;
const AUTO_DISMISS_MS = 4000;
const REDUCED_MOTION_FADE_MS = 600;
const SHAKE_PX = 6;
const ZOOM_START = 0.4;
const ZOOM_END = 1.3;

export type VideoProbeState = "pending" | "ready" | "unavailable";

/**
 * Pure decision: which scare mode is armed given whether a video URL was configured at
 * all and what its startup probe resolved to. Exported so the fallback decision itself
 * is unit-testable without a DOM (`ScareScene` is presentation-only and otherwise
 * untested, same "pure vs. impure" split as `client/scare/scream.ts`'s
 * `screamPartials`/`synthScream`). A `"pending"` probe (not yet resolved) reads as
 * `"builtin"` — the safe default until the video is actually confirmed playable, per
 * the operator decision that the fallback must be known before a scare ever fires.
 */
export function resolveArmedMode(
  videoConfigured: boolean,
  probe: VideoProbeState,
): "video" | "builtin" {
  return videoConfigured && probe === "ready" ? "video" : "builtin";
}

export interface ScareSceneOptions {
  readonly reducedMotion: boolean;
  readonly imageUrl?: string;
  readonly audioUrl?: string;
  readonly audio?: AudioContext | null;
  /** Operator-provided full-screen scare video — the level-3 payoff (see
   * `public/scare/ASSETS.md` and this package's README for the one-line copy step that
   * restores it on a fresh clone). Probed once at construction via the video element's
   * own `loadedmetadata`/`error` events, so `playVideo()`'s fallback decision is already
   * known before a scare ever fires. */
  readonly videoUrl?: string;
}

export class ScareScene {
  readonly #root: HTMLElement;
  readonly #overlay: HTMLDivElement;
  readonly #canvas: HTMLCanvasElement;
  readonly #ctx: CanvasRenderingContext2D | null;
  readonly #reducedMotion: boolean;
  readonly #imageUrl: string | undefined;
  readonly #audioUrl: string | undefined;
  readonly #audio: AudioContext | null | undefined;
  readonly #videoUrl: string | undefined;
  readonly #video: HTMLVideoElement | null;
  readonly #videoSoundHint: HTMLButtonElement;

  #image: HTMLImageElement | null = null;
  #active = false;
  #mode: "builtin" | "video" = "builtin";
  #videoProbe: VideoProbeState = "pending";
  #rafId: number | null = null;
  #autoDismissTimer: ReturnType<typeof setTimeout> | null = null;
  #htmlAudio: HTMLAudioElement | null = null;
  #resolvePlay: (() => void) | null = null;
  #playPromise: Promise<void> | null = null;
  #previouslyFocused: HTMLElement | null = null;

  readonly #onClick = (): void => {
    this.dismiss();
  };

  readonly #onKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      this.dismiss();
      return;
    }
    trapTabKey(event, this.#overlay, this.#overlay, document.activeElement);
  };

  readonly #onVideoEnded = (): void => {
    this.dismiss();
  };

  readonly #onVideoLoaded = (): void => {
    this.#videoProbe = "ready";
  };

  readonly #onVideoError = (): void => {
    this.#videoProbe = "unavailable";
  };

  readonly #onSoundHintClick = (event: MouseEvent): void => {
    event.stopPropagation();
    const video = this.#video;
    if (video === null) return;
    video.muted = false;
    void video.play().catch(() => {
      // Still can't get sound (e.g. no user-gesture credit left this tick) — stay
      // muted rather than retry-looping; the scare itself still plays either way.
    });
    this.#videoSoundHint.style.display = "none";
  };

  constructor(root: HTMLElement, opts: ScareSceneOptions) {
    this.#root = root;
    this.#reducedMotion = opts.reducedMotion;
    this.#imageUrl = opts.imageUrl;
    this.#audioUrl = opts.audioUrl;
    this.#audio = opts.audio;
    this.#videoUrl = opts.videoUrl;

    this.#overlay = document.createElement("div");
    this.#overlay.className = "scare-overlay";
    this.#overlay.setAttribute("role", "dialog");
    this.#overlay.setAttribute("aria-modal", "true");
    this.#overlay.setAttribute("aria-label", "Jump scare. Press Escape or click to dismiss.");
    this.#overlay.tabIndex = -1;
    this.#overlay.style.position = "fixed";
    this.#overlay.style.inset = "0";
    this.#overlay.style.zIndex = "2147483647";
    this.#overlay.style.background = "#000";
    this.#overlay.style.display = "none";
    this.#overlay.style.cursor = "pointer";

    this.#canvas = document.createElement("canvas");
    this.#canvas.style.width = "100%";
    this.#canvas.style.height = "100%";
    this.#canvas.style.display = "block";
    this.#overlay.appendChild(this.#canvas);
    this.#ctx = this.#canvas.getContext("2d");

    if (this.#videoUrl !== undefined) {
      const video = document.createElement("video");
      video.className = "scare-video";
      video.style.position = "absolute";
      video.style.inset = "0";
      video.style.width = "100%";
      video.style.height = "100%";
      video.style.objectFit = "cover";
      video.style.display = "none";
      video.playsInline = true;
      video.preload = "auto";
      video.muted = false;
      video.src = this.#videoUrl;
      video.addEventListener("loadedmetadata", this.#onVideoLoaded);
      video.addEventListener("error", this.#onVideoError);
      this.#overlay.appendChild(video);
      this.#video = video;
    } else {
      this.#video = null;
    }

    this.#videoSoundHint = document.createElement("button");
    this.#videoSoundHint.type = "button";
    this.#videoSoundHint.className = "scare-video-sound-hint";
    this.#videoSoundHint.textContent = "Click for sound";
    this.#videoSoundHint.style.position = "absolute";
    this.#videoSoundHint.style.right = "16px";
    this.#videoSoundHint.style.bottom = "16px";
    this.#videoSoundHint.style.padding = "8px 12px";
    this.#videoSoundHint.style.background = "rgba(0, 0, 0, 0.6)";
    this.#videoSoundHint.style.color = "#fff";
    this.#videoSoundHint.style.border = "1px solid rgba(255, 255, 255, 0.4)";
    this.#videoSoundHint.style.borderRadius = "6px";
    this.#videoSoundHint.style.font = "13px system-ui, sans-serif";
    this.#videoSoundHint.style.display = "none";
    this.#videoSoundHint.addEventListener("click", this.#onSoundHintClick);
    this.#overlay.appendChild(this.#videoSoundHint);

    this.#root.appendChild(this.#overlay);

    if (this.#imageUrl !== undefined) {
      const img = new Image();
      img.src = this.#imageUrl;
      this.#image = img;
    }
  }

  get active(): boolean {
    return this.#active;
  }

  /** Which scare a `play()`-triggered scare would show right now — `"builtin"` when no
   * `videoUrl` was configured, or when it was but hasn't (yet, or ever) probed as
   * `"ready"`. Meant for a debug HUD, not gameplay logic — `playVideo()` re-checks this
   * itself rather than trusting a caller's stale read of it. */
  get armedMode(): "video" | "builtin" {
    return resolveArmedMode(this.#videoUrl !== undefined, this.#videoProbe);
  }

  /** Safe to call twice: a second call while already active just awaits the same dismissal. */
  play(): Promise<void> {
    if (this.#active && this.#playPromise !== null) return this.#playPromise;

    this.#active = true;
    this.#mode = "builtin";
    this.#playPromise = new Promise<void>((resolve) => {
      this.#resolvePlay = resolve;
    });

    this.#startBuiltinPresentation();

    return this.#playPromise;
  }

  /** The built-in scare's actual on-screen/audio setup, factored out of `play()` so
   * `playVideo()`'s deep-failure fallback (video armed but even a muted `play()` throws)
   * can drop into it directly — reusing the promise/resolve `playVideo()` already handed
   * its caller, instead of `play()` minting a second one that caller would never see
   * resolve. Assumes `#active`/`#mode`/`#playPromise`/`#resolvePlay` are already set. */
  #startBuiltinPresentation(): void {
    if (this.#video !== null) this.#video.style.display = "none";
    this.#videoSoundHint.style.display = "none";
    this.#canvas.style.display = "block";

    const w = Math.max(1, window.innerWidth || this.#root.clientWidth || 1);
    const h = Math.max(1, window.innerHeight || this.#root.clientHeight || 1);
    this.#canvas.width = w;
    this.#canvas.height = h;

    this.#overlay.style.transition = "";
    this.#overlay.style.opacity = this.#reducedMotion ? "0" : "1";
    this.#overlay.style.display = "block";

    this.#previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#overlay.focus();

    this.#overlay.addEventListener("click", this.#onClick);
    document.addEventListener("keydown", this.#onKeydown);
    this.#autoDismissTimer = setTimeout(() => this.dismiss(), AUTO_DISMISS_MS);

    this.#playScream();

    if (this.#reducedMotion) {
      this.#drawStatic();
      requestAnimationFrame(() => {
        this.#overlay.style.transition = `opacity ${REDUCED_MOTION_FADE_MS}ms ease-in`;
        this.#overlay.style.opacity = "1";
      });
    } else {
      const start = performance.now();
      const tick = (now: number): void => {
        const elapsed = now - start;
        this.#drawAnimatedFrame(elapsed);
        this.#rafId = elapsed < ZOOM_DURATION_MS ? requestAnimationFrame(tick) : null;
      };
      this.#rafId = requestAnimationFrame(tick);
    }
  }

  /**
   * The level-3 payoff: the operator's full-screen scare video, sound on. Falls back to
   * the built-in scare (`play()`) whenever the video isn't armed — never probed ready,
   * failed to load, or wasn't configured at all — per the operator decision that level 3
   * must never show nothing. Unlike `play()`, there is no 4s auto-dismiss here: the
   * video owns its own runtime and dismisses itself on its `ended` event (or click /
   * Escape, same as the built-in scare). Reduced motion has nothing to suppress here —
   * the video always plays at full length and volume regardless of that setting, per the
   * operator decision that only the built-in scare's shake/zoom is reduced-motion-gated.
   */
  playVideo(): Promise<void> {
    if (this.armedMode !== "video" || this.#video === null) return this.play();
    if (this.#active && this.#playPromise !== null) return this.#playPromise;

    this.#active = true;
    this.#mode = "video";
    this.#playPromise = new Promise<void>((resolve) => {
      this.#resolvePlay = resolve;
    });

    const video = this.#video;
    video.muted = false;
    video.currentTime = 0;
    this.#canvas.style.display = "none";
    this.#videoSoundHint.style.display = "none";
    video.style.display = "block";

    this.#overlay.style.transition = "";
    this.#overlay.style.opacity = "1";
    this.#overlay.style.display = "block";

    this.#previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#overlay.focus();

    this.#overlay.addEventListener("click", this.#onClick);
    document.addEventListener("keydown", this.#onKeydown);
    video.addEventListener("ended", this.#onVideoEnded);

    const playAttempt = video.play();
    if (playAttempt !== undefined) {
      void playAttempt.catch(() => {
        // Autoplay-with-sound blocked — retry muted (still a valid autoplay) and let
        // the click-for-sound affordance offer a route back to sound.
        video.muted = true;
        this.#videoSoundHint.style.display = this.#active ? "block" : "none";
        const mutedAttempt = video.play();
        if (mutedAttempt !== undefined) {
          void mutedAttempt.catch(() => {
            // Still failed even muted — no blank/frozen screen: fall back to the
            // built-in scare instead, reusing the promise/resolve this call already
            // handed its caller (NOT `play()`, which would mint a second promise the
            // caller never sees).
            this.#teardownVideoPlayback();
            this.#mode = "builtin";
            this.#startBuiltinPresentation();
          });
        }
      });
    }

    return this.#playPromise;
  }

  /** Idempotent: dismissing when not active is a no-op. */
  dismiss(): void {
    if (!this.#active) return;
    this.#active = false;

    if (this.#mode === "video") {
      this.#teardownVideoPlayback();
    } else {
      if (this.#rafId !== null) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = null;
      }
      if (this.#autoDismissTimer !== null) {
        clearTimeout(this.#autoDismissTimer);
        this.#autoDismissTimer = null;
      }
      if (this.#htmlAudio !== null) {
        this.#htmlAudio.pause();
        this.#htmlAudio.currentTime = 0;
        this.#htmlAudio = null;
      }
    }

    this.#overlay.removeEventListener("click", this.#onClick);
    document.removeEventListener("keydown", this.#onKeydown);
    this.#overlay.style.transition = "";
    this.#overlay.style.opacity = "0";
    this.#overlay.style.display = "none";

    const toRestore = this.#previouslyFocused;
    this.#previouslyFocused = null;
    if (toRestore !== null) {
      try {
        toRestore.focus();
      } catch {
        // Best-effort: the element may no longer be attached/focusable.
      }
    }

    const resolve = this.#resolvePlay;
    this.#resolvePlay = null;
    this.#playPromise = null;
    resolve?.();
  }

  #teardownVideoPlayback(): void {
    const video = this.#video;
    if (video === null) return;
    video.pause();
    video.currentTime = 0;
    video.muted = false;
    video.removeEventListener("ended", this.#onVideoEnded);
    video.style.display = "none";
    this.#videoSoundHint.style.display = "none";
  }

  #playScream(): void {
    if (this.#audioUrl !== undefined) {
      try {
        const audio = new Audio(this.#audioUrl);
        this.#htmlAudio = audio;
        void audio.play().catch(() => {
          // Autoplay/decode failure — the visual scare still lands.
        });
      } catch {
        // No `Audio` constructor in this environment — silent degrade.
      }
      return;
    }

    const ctx = this.#resolveAudioContext();
    if (ctx === null) return;
    try {
      synthScream(ctx);
    } catch {
      // A WebAudio scheduling failure never breaks the scare UI.
    }
  }

  #resolveAudioContext(): AudioContext | null {
    if (this.#audio !== undefined) return this.#audio;
    if (typeof AudioContext === "undefined") return null;
    try {
      return new AudioContext();
    } catch {
      return null;
    }
  }

  #drawStatic(): void {
    const ctx = this.#ctx;
    if (ctx === null) return;
    const w = this.#canvas.width;
    const h = this.#canvas.height;
    ctx.clearRect(0, 0, w, h);
    this.#drawSubject(ctx, w, h, 0);
  }

  #drawAnimatedFrame(elapsedMs: number): void {
    const ctx = this.#ctx;
    if (ctx === null) return;
    const w = this.#canvas.width;
    const h = this.#canvas.height;
    const progress = Math.min(elapsedMs / ZOOM_DURATION_MS, 1);
    const zoom = ZOOM_START + (ZOOM_END - ZOOM_START) * progress;
    const decay = 1 - progress * 0.4;
    const shakeX = Math.sin(elapsedMs * 0.09) * SHAKE_PX * decay;
    const shakeY = Math.cos(elapsedMs * 0.13) * SHAKE_PX * decay;

    ctx.save();
    ctx.clearRect(0, 0, w, h);
    ctx.translate(w / 2 + shakeX, h / 2 + shakeY);
    ctx.scale(zoom, zoom);
    ctx.translate(-w / 2, -h / 2);
    this.#drawSubject(ctx, w, h, elapsedMs / 1000);
    ctx.restore();
  }

  #drawSubject(ctx: CanvasRenderingContext2D, w: number, h: number, t: number): void {
    const image = this.#image;
    if (image !== null && image.complete && image.naturalWidth > 0) {
      ctx.drawImage(image, 0, 0, w, h);
      return;
    }
    drawFace(ctx, w, h, t);
  }
}
